import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CheckCircle,
  Clock,
  AlertTriangle,
  XCircle,
  Home,
  Shield,
  Loader2
} from 'lucide-react';
import Button from '../../components/ui/Button';
import PageHeader from '../../components/ui/PageHeader';
import { useApp } from '../../context/AppContext';
import { useBroker } from '../../context/BrokerContext';
import './TransactionStatus.css';

const STATUS_CONFIG = {
  pending_payment: {
    icon: Clock,
    color: '#f59e0b',
    bgColor: '#fef3c7',
    title: 'Awaiting Payment',
    description: 'Please complete the payment and upload your receipt',
  },
  pending_review: {
    icon: Clock,
    color: '#3b82f6',
    bgColor: '#dbeafe',
    title: 'Under Review',
    description: 'Your payment slip has been received. A confirmation email containing your payment details has been sent. You will be notified once your payment is approved.',
  },
  manual_review: {
    icon: Clock,
    color: '#f59e0b',
    bgColor: '#fef3c7',
    title: 'Under Review',
    description: 'Your payment slip has been received. A confirmation email containing your payment details has been sent. You will be notified once your payment is approved.',
  },
  completed: {
    icon: CheckCircle,
    color: '#10b981',
    bgColor: '#d1fae5',
    title: 'Payment Confirmed',
    description: 'Your top-up has been successfully processed',
  },
  rejected: {
    icon: XCircle,
    color: '#ef4444',
    bgColor: '#fee2e2',
    title: 'Payment Rejected',
    description: 'There was an issue with your payment',
  },
  mismatched: {
    icon: AlertTriangle,
    color: '#f59e0b',
    bgColor: '#fef3c7',
    title: 'Verification Issue',
    description: 'Payment details do not match verified account',
  },
  penalty_applied: {
    icon: XCircle,
    color: '#ef4444',
    bgColor: '#fee2e2',
    title: 'Penalty Applied',
    description: 'Details mismatch - funds being returned with penalty',
  },
};

function TransactionStatus() {
  const { transactionId } = useParams();
  const navigate = useNavigate();
  const { transactions, currentTransaction } = useApp();
  const { getBrokerPath } = useBroker();
  const [transaction, setTransaction] = useState(null);

  useEffect(() => {
    const found = transactions.find(t => t.id === transactionId) || currentTransaction;
    if (found) {
      setTransaction(found);
    }
  }, [transactionId, transactions, currentTransaction]);

  if (!transaction) {
    return (
      <div className="status-container animate-fade-in">
        <div className="status-main-card">
          <div className="status-header">
            <div className="header-shapes">
              <div className="shape shape-1"></div>
              <div className="shape shape-2"></div>
              <div className="shape shape-3"></div>
            </div>
            <div className="header-icon-wrapper">
              <div className="header-icon">
                <AlertTriangle size={32} />
              </div>
            </div>
            <h1 className="header-title">Not Found</h1>
            <p className="header-subtitle">Transaction doesn't exist</p>
          </div>
          <div className="status-content">
            <div className="not-found-message">
              <p>The transaction you're looking for doesn't exist or has been removed.</p>
              <Button variant="primary" fullWidth icon={Home} onClick={() => navigate(getBrokerPath('/'))}>
                Go Home
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Determine status for display
  const hasMismatch = transaction.verificationStatus === 'mismatched' || transaction.mismatchDetails;
  const hasPenalty = transaction.mismatchDetails?.penaltyDetails;

  const statusKey = hasPenalty ? 'penalty_applied' :
                    hasMismatch ? 'mismatched' :
                    transaction.status || transaction.verificationStatus || 'pending_review';

  const statusConfig = STATUS_CONFIG[statusKey] || STATUS_CONFIG.pending_review;
  const StatusIcon = statusConfig.icon;

  return (
    <div className="status-container animate-fade-in">
      <div className="status-main-card">
        {/* Header Section */}
        <PageHeader showBackButton={false} showUserInfo={false}>
          {/* Centered Status Display */}
          <div className="status-center-display">
            {(statusKey === 'pending_review' || statusKey === 'manual_review') ? (
              <>
                <div className="status-loader-icon">
                  <Loader2 size={48} className="spinning" />
                </div>
                <h2 className="status-center-title">{statusConfig.title}</h2>
                <p className="status-center-desc">{statusConfig.description}</p>
              </>
            ) : (
              <>
                <div className="status-result-icon" style={{ backgroundColor: statusConfig.bgColor }}>
                  <StatusIcon size={32} style={{ color: statusConfig.color }} />
                </div>
                <h2 className="status-center-title">{statusConfig.title}</h2>
                <p className="status-center-desc">{statusConfig.description}</p>
              </>
            )}

            {/* Reference Number & Amount */}
            <div className="status-header-details">
              <div className="status-ref-row">
                <span className="status-ref-label">Reference:</span>
                <span className="status-ref-value">{transaction.referenceNumber}</span>
              </div>
              <div className="status-amount-row">
                <span className="status-amount-value">PKR {(transaction.amount || 0).toLocaleString()}</span>
              </div>
            </div>
          </div>
        </PageHeader>

        {/* Content Section */}
        <div className="status-content">
          {/* Progress Indicator for pending states */}
          {(transaction.status === 'pending_review' || transaction.status === 'manual_review') &&
           !hasMismatch && !hasPenalty && (
            <div className="verification-progress">
              <div className="section-header">
                <Shield size={16} />
                <span>Verification Progress</span>
              </div>
              <div className="progress-steps-row">
                <div className="progress-step completed">
                  <CheckCircle size={18} />
                  <span>Received</span>
                </div>
                <div className="progress-connector active"></div>
                <div className="progress-step active">
                  <Clock size={18} />
                  <span>Verifying</span>
                </div>
                <div className="progress-connector"></div>
                <div className="progress-step">
                  <CheckCircle size={18} />
                  <span>Confirmed</span>
                </div>
              </div>
            </div>
          )}

          {/* Mismatch Warning */}
          {hasMismatch && !hasPenalty && (
            <div className="mismatch-alert">
              <AlertTriangle size={20} />
              <div>
                <strong>Receipt details do not match</strong>
                <p>Please contact support for assistance</p>
              </div>
            </div>
          )}

          {/* Penalty Details */}
          {hasPenalty && (
            <div className="penalty-section">
              <div className="section-header penalty">
                <AlertTriangle size={16} />
                <span>Penalty Applied</span>
              </div>
              <div className="penalty-breakdown">
                <div className="penalty-row">
                  <span>Original Amount</span>
                  <span>PKR {transaction.mismatchDetails.penaltyDetails.originalAmount.toLocaleString()}</span>
                </div>
                <div className="penalty-row deduction">
                  <span>Penalty ({transaction.mismatchDetails.penaltyDetails.penaltyRate}%)</span>
                  <span>- PKR {transaction.mismatchDetails.penaltyDetails.penaltyAmount.toLocaleString()}</span>
                </div>
                <div className="penalty-row return">
                  <span>Amount Being Returned</span>
                  <span>PKR {transaction.mismatchDetails.penaltyDetails.returnAmount.toLocaleString()}</span>
                </div>
              </div>
              <p className="penalty-note">Funds will be returned within 3-5 business days.</p>
            </div>
          )}

        </div>

        {/* Footer - Info Message */}
        <div className="status-footer">
          <p className="footer-message">
            You may close this tab. Our team will contact you within 5-15 minutes via email.
          </p>
        </div>
      </div>
    </div>
  );
}

export default TransactionStatus;
